// /admin/empresas/:empresaId/crear-pedido?presupuesto_id=X
// Wizard para crear un pedido one-shot B2B desde un presupuesto modalidad=compra_unica.
//
// Crea fila en `pedidos` (NO en suscripciones_empresa) con:
//   - tipo: 'compra_unica'
//   - empresa_id + presupuesto_id linkeados
//   - items jsonb del presupuesto
//   - dirección, fecha, ventana, precio editable
//   - estado inicial: 'borrador'
// El cobro y la facturación siguen el flujo normal de pedidos.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { isAfterCutoff, CUTOFF_HOUR } from '../../lib/entregas'

const VENTANAS = [
  { v: 'manana', l: 'Mañana (10–14hs)' },
  { v: 'tarde',  l: 'Tarde (14–18hs)' },
  { v: 'noche',  l: 'Tarde-noche (18–21hs)' },
]

const DIAS_MAP = {
  0: 'domingo', 1: 'lunes', 2: 'martes', 3: 'miercoles', 4: 'jueves', 5: 'viernes', 6: 'sabado',
}

export default function CrearPedidoB2B() {
  const { empresaId } = useParams()
  const [searchParams] = useSearchParams()
  const presupuestoId = searchParams.get('presupuesto_id')
  const nav = useNavigate()

  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)
  const [creando, setCreando] = useState(false)
  const [exito, setExito] = useState(null)

  const [empresa, setEmpresa] = useState(null)
  const [presupuesto, setPresupuesto] = useState(null)
  const [direcciones, setDirecciones] = useState([])
  const [edificios, setEdificios] = useState([])
  const [tarifas, setTarifas] = useState({})  // {clave: monto}

  const [form, setForm] = useState({
    direccion_id: '',
    edificio_id: '',
    fecha_entrega: '',
    ventana_horaria: 'manana',
    metodo_cobro: 'transferencia',
    subtotal_items: 0,       // lo que negociaste con el cliente (sin envío)
    costo_envio: 0,          // recargo por fuera de ruta (auto-calculado, editable)
    envio_override: false,   // si el operador modificó manualmente, no recalcular
    notas: '',
  })

  useEffect(() => {
    (async () => {
      try {
        setCargando(true)
        const [{ data: e, error: eE }, { data: p, error: eP }, { data: dirs, error: eD }, { data: tfs }] = await Promise.all([
          supabase.from('empresas').select('id, razon_social, rut, tipo, contacto_principal_nombre, telefono, email_facturacion, facturacion_modo').eq('id', empresaId).single(),
          presupuestoId
            ? supabase.from('presupuestos').select('*, items:presupuestos_items(*, producto:productos(nombre, marca, presentacion))').eq('id', presupuestoId).single()
            : Promise.resolve({ data: null, error: null }),
          supabase.from('empresas_direcciones').select('id, alias, calle, numero, apto_unidad, barrio_id, fuera_de_zona, lat, lng, geocoded_at, barrio:barrios(id, nombre, dias)').eq('empresa_id', empresaId).eq('activa', true).order('alias'),
          supabase.from('envio_tarifas').select('clave, monto'),
        ])
        if (eE) throw new Error('Empresa: ' + eE.message)
        if (eP) throw new Error('Presupuesto: ' + eP.message)
        if (eD) throw new Error('Direcciones: ' + eD.message)

        setEmpresa(e)
        setPresupuesto(p)
        setDirecciones(dirs || [])
        const { data: eds } = await supabase.from('edificios').select('id, nombre, razon_social, rut, facturacion_email, direccion, apto, portera_nombre, portera_telefono, acceso_notas').eq('empresa_id', empresaId).eq('activo', true).order('nombre')
        setEdificios(eds || [])
        // Indexar tarifas por clave
        const tarifasIdx = {}
        ;(tfs || []).forEach(t => { tarifasIdx[t.clave] = Number(t.monto) || 0 })
        setTarifas(tarifasIdx)

        // Pre-llenar form con datos del presupuesto
        setForm(f => ({
          ...f,
          direccion_id: dirs?.[0]?.id || '',
          edificio_id: p?.edificio_id || '',
          facturacion_con_rut: p?.facturacion_con_rut ?? null,
          subtotal_items: p?.total || 0,
        }))
      } catch (err) {
        setError(err.message)
      } finally {
        setCargando(false)
      }
    })()
  }, [empresaId, presupuestoId])

  const direccionElegida = useMemo(() => direcciones.find(d => d.id === form.direccion_id), [direcciones, form.direccion_id])
  const edificioElegido = useMemo(() => edificios.find(e => e.id === form.edificio_id), [edificios, form.edificio_id])

  // Calcular día de la semana de la fecha elegida + si cae en ruta del barrio
  const fechaInfo = useMemo(() => {
    if (!form.fecha_entrega) return null
    const d = new Date(form.fecha_entrega + 'T00:00:00')
    const diaSem = DIAS_MAP[d.getDay()]
    // Fuera de zona: la dirección no está en la ruta de barrios (decisión del superadmin en la
    // ficha de la empresa). No aplica el concepto "en ruta / fuera de ruta" ni su recargo.
    const fueraDeZona = !!direccionElegida?.fuera_de_zona
    const enRuta = fueraDeZona ? false : (direccionElegida?.barrio?.dias?.includes(diaSem) || false)
    return { diaSem, enRuta, fueraDeZona, barrio: direccionElegida?.barrio?.nombre }
  }, [form.fecha_entrega, direccionElegida])

  // Recalcula costo_envio según fecha vs días del barrio (a menos que el operador hizo override manual).
  // Si la dirección es fuera de zona, NO auto-cobramos el recargo de fuera de ruta: el envío lo define
  // Nico en cada pedido (entrega coordinada aparte).
  useEffect(() => {
    if (form.envio_override) return
    if (!fechaInfo) return
    if (fechaInfo.fueraDeZona) return
    const tarifaFueraRuta = tarifas['suscriptor_fuera_ruta'] || 165
    const nuevoEnvio = fechaInfo.enRuta ? 0 : tarifaFueraRuta
    setForm(f => f.costo_envio === nuevoEnvio ? f : { ...f, costo_envio: nuevoEnvio })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fechaInfo?.enRuta, fechaInfo?.fueraDeZona, tarifas, form.envio_override])

  // Items del presupuesto formateados para guardar en pedidos.items (jsonb)
  const items = useMemo(() => {
    if (!presupuesto?.items) return []
    return [...presupuesto.items]
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
      .map(it => {
        let desc = ''
        if (it.tipo === 'producto' && it.producto) {
          desc = [it.producto.marca, it.producto.nombre, it.producto.presentacion].filter(Boolean).join(' · ')
        } else if (it.tipo === 'kit' && it.kit_codigo) {
          desc = `Kit ${it.kit_codigo}`
        } else {
          desc = it.descripcion || ''
        }
        return {
          tipo: it.tipo,
          producto_id: it.producto_id || null,
          kit_codigo: it.kit_codigo || null,
          descripcion: desc,
          cantidad: Number(it.cantidad) || 0,
          precio_unitario: Number(it.precio_unitario) || 0,
          descuento_pct: Number(it.descuento_pct || 0),
          iva_pct: Number(it.iva_pct) || 22,
          subtotal: Number(it.subtotal) || 0,
        }
      })
  }, [presupuesto])

  // ── Validación de stock ────────────────────────────────────────────────────
  // Trae stock_actual y stock_reservado de los productos del presupuesto y
  // detecta faltantes ANTES de crear el pedido (bug detectado 16-jul: se creó
  // pedido con 4 Paños rosados existiendo solo 2 y no hubo warning).
  const [stockPorProducto, setStockPorProducto] = useState({}) // {producto_id: {actual, reservado, nombre}}
  useEffect(() => {
    if (!presupuesto?.items?.length) return
    const productoIds = [...new Set(presupuesto.items.filter(i => i.tipo === 'producto' && i.producto_id).map(i => i.producto_id))]
    if (productoIds.length === 0) return
    ;(async () => {
      const { data, error } = await supabase
        .from('productos')
        .select('id, nombre, marca, stock_actual, stock_reservado')
        .in('id', productoIds)
      if (error) { console.error('cargar stock:', error); return }
      const idx = {}
      ;(data || []).forEach(p => {
        idx[p.id] = {
          nombre: [p.marca, p.nombre].filter(Boolean).join(' '),
          actual: Number(p.stock_actual || 0),
          reservado: Number(p.stock_reservado || 0),
          disponible: Number(p.stock_actual || 0) - Number(p.stock_reservado || 0),
        }
      })
      setStockPorProducto(idx)
    })()
  }, [presupuesto?.id])

  // Consolidar cantidades pedidas por producto (por si hay líneas duplicadas)
  const faltantesStock = useMemo(() => {
    if (!presupuesto?.items) return []
    const pedidoPorProd = {}
    presupuesto.items.forEach(it => {
      if (it.tipo === 'producto' && it.producto_id) {
        pedidoPorProd[it.producto_id] = (pedidoPorProd[it.producto_id] || 0) + (Number(it.cantidad) || 0)
      }
    })
    const faltas = []
    Object.entries(pedidoPorProd).forEach(([pid, pedido]) => {
      const s = stockPorProducto[pid]
      if (!s) return // stock aún no cargado
      if (pedido > s.disponible) {
        faltas.push({ producto_id: pid, nombre: s.nombre, pedido, disponible: s.disponible, actual: s.actual, reservado: s.reservado })
      }
    })
    return faltas
  }, [stockPorProducto, presupuesto])

  const subtotalItemsCalc = useMemo(() => items.reduce((acc, it) => acc + (it.subtotal || 0), 0), [items])
  const subtotalNegociado = Number(form.subtotal_items) || 0
  const costoEnvio = Number(form.costo_envio) || 0
  const totalFinal = subtotalNegociado + costoEnvio
  const descuentoMonto = subtotalItemsCalc - subtotalNegociado
  const descuentoPct = subtotalItemsCalc > 0 ? (descuentoMonto / subtotalItemsCalc) * 100 : 0
  const hayDesc = descuentoMonto > 0.01

  function setF(k, v) { setForm(f => ({ ...f, [k]: v })) }
  function setEnvioManual(v) {
    setForm(f => ({ ...f, costo_envio: Number(v) || 0, envio_override: true }))
  }
  function resetEnvio() {
    setForm(f => ({ ...f, envio_override: false }))
  }

  async function crear() {
    setError(null)
    if (!form.direccion_id) { setError('Tenés que elegir una dirección de entrega'); return }
    if (!form.fecha_entrega) { setError('Tenés que elegir la fecha de entrega'); return }
    if (!subtotalNegociado || subtotalNegociado <= 0) { setError('Subtotal items inválido'); return }
    if (items.length === 0) { setError('El presupuesto no tiene items'); return }

    setCreando(true)
    try {
      const dir = direccionElegida
      const edSel = edificios.find(e => e.id === form.edificio_id) || null
      const conRut = form.facturacion_con_rut === true ? true : form.facturacion_con_rut === false ? false : !!(edSel && edSel.rut)
      if (!dir) throw new Error('Dirección no encontrada')

      const { data: { user } } = await supabase.auth.getUser()

      // Generar número de orden con la función SQL existente
      const { data: numOrden, error: nErr } = await supabase.rpc('generar_numero_orden')
      if (nErr) throw new Error('No se pudo generar número de orden: ' + nErr.message)

      // Calcular fecha_corte = D-1 a las 16hs (modelo D-1 fijado 18-may-2026).
      // NOTA: D-1 acá es corte LOGÍSTICO (deadline de armado/modificación del pedido), no D-1 de cobro.
      // Con el nuevo modelo MP (cobro inmediato + cada 30d, post-Roberto 9-jul-2026), el cobro es
      // independiente del corte logístico. Ver docs-proyecto/00-MAESTRO/DECISIONES.md.
      const fechaEntrega = new Date(form.fecha_entrega + 'T00:00:00')
      const fechaCorte = new Date(fechaEntrega)
      fechaCorte.setDate(fechaCorte.getDate() - 1)
      fechaCorte.setHours(16, 0, 0, 0)

      // Día de la semana de la fecha de entrega
      const diaSemana = DIAS_MAP[fechaEntrega.getDay()]
      const dirFueraDeZona = !!dir.fuera_de_zona
      const enRuta = dirFueraDeZona ? false : (dir.barrio?.dias?.includes(diaSemana) || false)

      // Comprador: razón social + contacto principal
      const compradorNombre = empresa.contacto_principal_nombre
        ? `${empresa.razon_social} — ${empresa.contacto_principal_nombre}`
        : empresa.razon_social

      const payload = {
        numero_orden: numOrden,
        empresa_id: empresaId,
        edificio_id: form.edificio_id || null,
        ...(edSel ? { facturacion_razon_social: edSel.razon_social || null, facturacion_rut: conRut && edSel.rut ? edSel.rut.replace(/\D/g, '') : null, facturacion_email: edSel.facturacion_email || null } : {}),
        presupuesto_id: presupuestoId || null,
        comprador_nombre: compradorNombre,
        comprador_telefono: empresa.telefono || null,
        comprador_email: empresa.email_facturacion || null,
        tipo: 'compra_unica',
        items,
        entrega_barrio_id: dir.barrio_id,
        entrega_direccion: `${dir.calle} ${dir.numero || ''}`.trim(),
        entrega_apto: dir.apto_unidad || null,
        entrega_lat: dir.lat || null,
        entrega_lng: dir.lng || null,
        entrega_dia_semana: diaSemana,
        entrega_ventana: form.ventana_horaria,
        monto_subtotal: subtotalNegociado,
        costo_envio: costoEnvio,
        monto_total: totalFinal,
        envio_es_en_ruta: enRuta,
        fecha_entrega_programada: form.fecha_entrega,
        fecha_corte: fechaCorte.toISOString(),
        estado: 'borrador',
        metodo_pago: form.metodo_cobro || 'transferencia',
        notas: [
          presupuesto ? `Generado desde presupuesto Nº ${presupuesto.numero}` : null,
          hayDesc ? `Descuento global aplicado: ${descuentoPct.toFixed(2)}% (−$${descuentoMonto.toFixed(2)})` : null,
          dirFueraDeZona
            ? `🚚 Dirección FUERA DE ZONA de reparto — entrega coordinada aparte. Envío definido a mano: $${costoEnvio}`
            : (!enRuta ? `⚠ Fuera de ruta del barrio (${dir.barrio?.nombre || 'sin barrio'}) — recargo envío $${costoEnvio}` : null),
          `Método de cobro: ${form.metodo_cobro}`,
          form.notas || null,
        ].filter(Boolean).join('\n'),
      }

      const { data: nuevo, error: insErr } = await supabase
        .from('pedidos')
        .insert(payload)
        .select('id, numero_orden')
        .single()
      if (insErr) throw insErr

      // Marcar presupuesto como aceptado si no lo está
      if (presupuesto && presupuesto.estado !== 'aceptado') {
        await supabase.from('presupuestos').update({
          estado: 'aceptado',
          aceptado_at: presupuesto.aceptado_at || new Date().toISOString(),
        }).eq('id', presupuesto.id)
      }

      // Cambiar pipeline de la empresa a 'activo'
      if (empresa.estado_pipeline !== 'activo') {
        await supabase.from('empresas').update({ estado_pipeline: 'activo' }).eq('id', empresaId)
      }

      // Registrar interacción CRM
      await supabase.from('empresas_interacciones').insert({
        empresa_id: empresaId,
        tipo: 'nota',
        autor_id: user?.id || null,
        resumen: `Se creó pedido B2B Nº ${nuevo.numero_orden}`,
        notas: `Desde presupuesto Nº ${presupuesto?.numero || '(sin presupuesto)'} · Subtotal $${subtotalNegociado.toLocaleString('es-UY')} + envío $${costoEnvio.toLocaleString('es-UY')} = total $${totalFinal.toLocaleString('es-UY')} · Entrega ${form.fecha_entrega}${!enRuta ? ' (FUERA DE RUTA)' : ''}`,
        presupuesto_id: presupuestoId || null,
      })

      setExito({ numero_orden: nuevo.numero_orden, id: nuevo.id })
    } catch (err) {
      setError(err.message || 'Error al crear el pedido')
    } finally {
      setCreando(false)
    }
  }

  if (cargando) {
    return <AdminLayout title="Crear pedido B2B"><div style={{ padding: 40, textAlign: 'center', color: '#64748B' }}>Cargando…</div></AdminLayout>
  }
  if (!empresa) {
    return <AdminLayout title="Crear pedido B2B"><div style={{ padding: 24, color: '#991B1B' }}>{error || 'Empresa no encontrada'}</div></AdminLayout>
  }
  if (exito) {
    return (
      <AdminLayout title="Pedido creado">
        <Estilos />
        <div className="cp-exito">
          <div className="cp-exito-icon">✓</div>
          <h2>Pedido Nº {exito.numero_orden} creado</h2>
          <p>Quedó en estado <strong>borrador</strong>. Confirmá el pago manual (transferencia/etc) para que pase a <strong>pagado</strong> y entre al flujo de armado y entrega.</p>
          <div className="cp-exito-actions">
            <button className="cp-btn-primary" onClick={() => nav(`/admin/empresas/${empresaId}`)}>Volver a {empresa.razon_social}</button>
            <button className="cp-btn-secondary" onClick={() => nav(`/admin/pedidos`)}>Ver en /admin/pedidos</button>
          </div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout title="Crear pedido B2B">
      <Estilos />
      <div className="cp-page">
        <button className="cp-back" onClick={() => nav(`/admin/empresas/${empresaId}`)}>← Volver a {empresa.razon_social}</button>

        <header className="cp-head">
          <h1>Crear pedido B2B one-shot</h1>
          <p>
            Empresa: <strong>{empresa.razon_social}</strong>
            {presupuesto && <> · desde presupuesto Nº <strong>{presupuesto.numero}</strong></>}
            · 🛍 Compra única
          </p>
        </header>

        {/* Warning de stock insuficiente */}
        {faltantesStock.length > 0 && (
          <section className="cp-card" style={{ border: '2px solid #DC2626', background: '#FEF2F2' }}>
            <h3 style={{ color: '#991B1B' }}>⚠ Stock insuficiente en {faltantesStock.length} producto(s)</h3>
            <p style={{ margin: '0 0 12px 0', fontSize: 13, color: '#7F1D1D' }}>
              El pedido pide más unidades de las que hay disponibles ahora (stock actual − stock ya reservado en otros pedidos). Podés crearlo igual, pero avisá a Gimena para reponer antes de la fecha de entrega.
            </p>
            <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', background: '#FEE2E2' }}>
                  <th style={{ padding: '6px 8px' }}>Producto</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Pedido</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Disponible</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Falta</th>
                </tr>
              </thead>
              <tbody>
                {faltantesStock.map(f => (
                  <tr key={f.producto_id}>
                    <td style={{ padding: '6px 8px' }}>{f.nombre}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>{f.pedido}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: '#991B1B' }}>
                      {f.disponible}
                      <span style={{ fontSize: 10, color: '#7F1D1D', display: 'block' }}>
                        ({f.actual} en depósito − {f.reservado} reservado)
                      </span>
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 800, color: '#DC2626' }}>
                      −{f.pedido - f.disponible}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Items pre-cargados */}
        <section className="cp-card">
          <h3>📦 Items del pedido ({items.length})</h3>
          <table className="cp-items">
            <thead><tr><th>Descripción</th><th>Cant.</th><th>P. unit.</th><th>Desc.</th><th>Subtotal</th></tr></thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i}>
                  <td>{it.descripcion}</td>
                  <td className="cp-num">{it.cantidad}</td>
                  <td className="cp-num">${it.precio_unitario.toLocaleString('es-UY')}</td>
                  <td className="cp-num">{it.descuento_pct > 0 ? it.descuento_pct.toFixed(0) + '%' : '—'}</td>
                  <td className="cp-num"><strong>${it.subtotal.toLocaleString('es-UY', { maximumFractionDigits: 2 })}</strong></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} className="cp-num">Subtotal items</td>
                <td className="cp-num">${subtotalItemsCalc.toLocaleString('es-UY', { maximumFractionDigits: 2 })}</td>
              </tr>
            </tfoot>
          </table>
        </section>

        {/* Datos del pedido */}
        <section className="cp-card">
          <h3>🚚 Entrega y cobro</h3>

          <Field label="Dirección de entrega *">
            <select value={form.direccion_id} onChange={e => setF('direccion_id', e.target.value)}>
              <option value="">— Elegir —</option>
              {direcciones.map(d => (
                <option key={d.id} value={d.id}>
                  {d.alias} · {d.calle} {d.numero || ''}{d.fuera_de_zona ? ' · 🚚 fuera de zona' : ''}{d.lat && d.lng ? '' : ' ⚠ sin mapa'}
                </option>
              ))}
            </select>
            {direcciones.length === 0 && (
              <div className="cp-warn">⚠ La empresa no tiene direcciones activas. Cargá una primero en la pestaña Direcciones.</div>
            )}
            {direccionElegida && (!direccionElegida.lat || !direccionElegida.lng) && (
              <div className="cp-warn">
                ⚠ Esta dirección no está geocodificada en el mapa. Andá a Direcciones → editar → "🔍 Buscar en mapa" antes de crear el pedido, así el repartidor sabe exactamente dónde entregar.
              </div>
            )}
          </Field>

          {empresa.tipo === 'administradora' && (
            <Field label="Edificio (facturar a) — administradora">
              <select value={form.edificio_id} onChange={e => setF('edificio_id', e.target.value)}>
                <option value="">— Sin edificio (facturar a la administradora) —</option>
                {edificios.map(ed => (
                  <option key={ed.id} value={ed.id}>
                    {ed.nombre}{ed.razon_social ? ` · ${ed.razon_social}` : ''}{ed.rut ? ` · RUT ${ed.rut}` : ' · e-Ticket'}
                  </option>
                ))}
              </select>
              {edificios.length === 0 && (
                <div className="cp-warn">⚠ Esta administradora no tiene edificios cargados. Cargalos en la ficha de la empresa → pestaña Edificios.</div>
              )}
              {edificioElegido && (
                <div className="cp-hint">
                  🧾 Se factura a {edificioElegido.razon_social || edificioElegido.nombre}{edificioElegido.rut ? ` (RUT ${edificioElegido.rut}, e-Factura)` : ' (e-Ticket)'}.
                  {edificioElegido.portera_nombre ? ` 🔑 Portería: ${edificioElegido.portera_nombre}${edificioElegido.portera_telefono ? ' · ' + edificioElegido.portera_telefono : ''}.` : ''}
                </div>
              )}
            </Field>
          )}
          <div className="cp-grid-2">
            <Field label="Fecha de entrega *">
              <input type="date" value={form.fecha_entrega} onChange={e => setF('fecha_entrega', e.target.value)} min={new Date().toISOString().slice(0,10)} />
              {fechaInfo && (
                <div className={`cp-hint${fechaInfo.enRuta ? ' ok' : ' warn'}`}>
                  {fechaInfo.fueraDeZona
                    ? `🚚 ${fechaInfo.diaSem} · dirección fuera de zona — entrega coordinada`
                    : fechaInfo.enRuta
                      ? `🟢 ${fechaInfo.diaSem} en ruta de ${fechaInfo.barrio}`
                      : `🟡 ${fechaInfo.diaSem} fuera de ruta de ${fechaInfo.barrio || 'el barrio'}`}
                </div>
              )}
            </Field>
            <Field label="Ventana horaria">
              <select value={form.ventana_horaria} onChange={e => setF('ventana_horaria', e.target.value)}>
                {VENTANAS.map(v => <option key={v.v} value={v.v}>{v.l}</option>)}
              </select>
            </Field>
          </div>

          <div className="cp-grid-2">
            <Field label="Subtotal items (UYU c/IVA) *">
              <input type="number" min="0" step="0.01" value={form.subtotal_items} onChange={e => setF('subtotal_items', e.target.value)} />
              {hayDesc && (
                <div className="cp-hint">
                  Descuento aplicado: −${descuentoMonto.toLocaleString('es-UY', { maximumFractionDigits: 2 })} ({descuentoPct.toFixed(2)}%)
                </div>
              )}
            </Field>
            <Field label="Método de cobro">
              <select value={form.metodo_cobro} onChange={e => setF('metodo_cobro', e.target.value)}>
                <option value="transferencia">🏦 Transferencia bancaria / MP</option>
                <option value="mercadopago">💳 Tarjeta (checkout MP)</option>
                <option value="efectivo">💵 Efectivo contra entrega</option>
                <option value="cheque">📄 Cheque</option>
                <option value="cuenta_corriente">📋 Cuenta corriente 30d</option>
              </select>
              <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>
                Este método define dónde aparece el cobro pendiente (Transferencias / Cobros B2B) y el mensaje al cliente.
              </div>
            </Field>
          </div>

          {/* Costo de envío con desglose */}
          <Field label={`Costo de envío (UYU)${form.envio_override ? ' · 🟡 manual' : ' · auto'}`}>
            <div className="cp-envio-row">
              <input type="number" min="0" step="0.01" value={form.costo_envio} onChange={e => setEnvioManual(e.target.value)} />
              {form.envio_override && (
                <button type="button" className="cp-link" onClick={resetEnvio}>↻ Auto</button>
              )}
            </div>
            <div className={`cp-hint${fechaInfo?.enRuta ? ' ok' : ' warn'}`}>
              {!fechaInfo
                ? 'Cargá la fecha para calcular envío'
                : fechaInfo.fueraDeZona
                  ? '🚚 Dirección fuera de zona — el envío lo definís vos (no se calcula solo)'
                  : fechaInfo.enRuta
                    ? `🟢 En ruta de ${fechaInfo.barrio} (${fechaInfo.diaSem}) — envío gratis`
                    : `🟡 Fuera de ruta de ${fechaInfo.barrio || 'el barrio'} (${fechaInfo.diaSem}) — recargo $${(tarifas['suscriptor_fuera_ruta'] || 165).toLocaleString('es-UY')}`}
            </div>
          </Field>

          {/* Desglose total */}
          <div className="cp-desglose">
            <div className="cp-desglose-row">
              <span>Subtotal items</span>
              <span>${subtotalNegociado.toLocaleString('es-UY', { maximumFractionDigits: 2 })}</span>
            </div>
            <div className="cp-desglose-row">
              <span>Envío {fechaInfo?.fueraDeZona ? '(fuera de zona)' : fechaInfo?.enRuta ? '(en ruta)' : '(fuera de ruta)'}</span>
              <span>{costoEnvio > 0 ? `+ $${costoEnvio.toLocaleString('es-UY')}` : 'gratis'}</span>
            </div>
            <div className="cp-desglose-row cp-desglose-total">
              <span>Total a cobrar</span>
              <span>${totalFinal.toLocaleString('es-UY', { maximumFractionDigits: 2 })}</span>
            </div>
          </div>

          <Field label="Notas internas">
            <textarea rows={2} value={form.notas} onChange={e => setF('notas', e.target.value)} placeholder="Ej: 'Coordinar con encargado por la mañana', etc." />
          </Field>
        </section>

        {error && <div className="cp-error">⚠ {error}</div>}

        <div className="cp-foot">
          <button className="cp-btn-secondary" onClick={() => nav(-1)} disabled={creando}>Cancelar</button>
          <button className="cp-btn-primary" onClick={crear} disabled={creando || direcciones.length === 0}>
            {creando ? 'Creando…' : '✓ Crear pedido B2B'}
          </button>
        </div>

        <div className="cp-info">
          <h4>📌 Después de crear</h4>
          <ol>
            <li>Pedido queda en estado <code>borrador</code>.</li>
            <li>Cuando se confirme el pago (transferencia/etc), Roberto lo marca <code>pagado</code> desde <code>/admin/pedidos</code>.</li>
            <li>El día del corte (D-1 a las 16hs) entra al flujo de armado y entrega normal.</li>
            <li>La e-Factura se emite manual (hasta integrar Zsoftware).</li>
          </ol>
        </div>
      </div>
    </AdminLayout>
  )
}

function Field({ label, children }) {
  return (
    <label className="cp-field">
      <span>{label}</span>
      {children}
    </label>
  )
}

function Estilos() {
  return (
    <style>{`
      .cp-page { max-width: 880px; margin: 0 auto; padding: 24px; font-family: Inter, system-ui, sans-serif; color: #1A3461; }
      .cp-back { background: transparent; border: none; color: #64748B; font-size: 13px; cursor: pointer; padding: 4px 0; margin-bottom: 8px; font-family: inherit; }
      .cp-back:hover { color: #1A3461; }
      .cp-head { margin-bottom: 18px; }
      .cp-head h1 { margin: 0 0 4px; font-size: 22px; font-weight: 900; letter-spacing: -0.02em; }
      .cp-head p { margin: 0; font-size: 13.5px; color: #64748B; }

      .cp-card { background: #fff; border: 1px solid #E2E8F0; border-radius: 12px; padding: 18px 20px; margin-bottom: 14px; }
      .cp-card h3 { margin: 0 0 12px; font-size: 14px; font-weight: 800; color: #1A3461; }

      .cp-items { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
      .cp-items th { background: #F8FAFC; padding: 8px 9px; font-size: 11px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 0.05em; text-align: left; border-bottom: 1px solid #E2E8F0; }
      .cp-items td { padding: 9px; border-bottom: 1px solid #F1F5F9; font-size: 12.5px; }
      .cp-items tfoot td { background: #F8FAFC; font-weight: 700; padding: 9px; font-size: 12.5px; }
      .cp-num { text-align: right; font-variant-numeric: tabular-nums; }

      .cp-field { display: block; margin-bottom: 14px; }
      .cp-field > span { display: block; font-size: 11px; font-weight: 700; color: #475569; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.05em; }
      .cp-field input, .cp-field select, .cp-field textarea {
        width: 100%; padding: 9px 11px; border-radius: 8px; border: 1.5px solid #E2E8F0;
        font-size: 13.5px; font-family: inherit; color: #1A3461; background: #fff; box-sizing: border-box;
      }
      .cp-field textarea { resize: vertical; min-height: 50px; }
      .cp-field input:focus, .cp-field select:focus, .cp-field textarea:focus { outline: none; border-color: #00A8B5; }
      .cp-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
      @media (max-width: 640px) { .cp-grid-2 { grid-template-columns: 1fr; } }

      .cp-envio-row { display: flex; gap: 8px; align-items: center; }
      .cp-envio-row input { flex: 1; }
      .cp-link { background: transparent; border: none; color: #00A8B5; cursor: pointer; font-family: inherit; font-size: 12.5px; font-weight: 700; padding: 4px 8px; white-space: nowrap; }
      .cp-link:hover { text-decoration: underline; }

      .cp-desglose { background: #F8FAFC; border-radius: 8px; padding: 12px 14px; margin: 14px 0 0; }
      .cp-desglose-row { display: flex; justify-content: space-between; font-size: 13px; padding: 4px 0; color: #475569; }
      .cp-desglose-row span:last-child { font-variant-numeric: tabular-nums; font-weight: 600; color: #1A3461; }
      .cp-desglose-total {
        border-top: 1px solid #E2E8F0; padding-top: 9px; margin-top: 4px;
        font-size: 15px; font-weight: 800; color: #1A3461;
      }
      .cp-desglose-total span:last-child { font-size: 17px; color: #00A8B5; }

      .cp-hint { font-size: 11.5px; color: #64748B; margin-top: 4px; padding: 4px 8px; background: #F8FAFC; border-radius: 6px; }
      .cp-hint.ok { background: #DCFCE7; color: #14532D; }
      .cp-hint.warn { background: #FEF3C7; color: #78350F; }
      .cp-warn { background: #FEF3C7; border-left: 3px solid #F59E0B; padding: 8px 12px; border-radius: 0 6px 6px 0; color: #78350F; font-size: 12.5px; margin-top: 6px; }
      .cp-error { background: #FEE2E2; border-left: 3px solid #DC2626; padding: 10px 14px; border-radius: 0 8px 8px 0; color: #991B1B; font-size: 13px; margin-bottom: 12px; }

      .cp-foot { display: flex; justify-content: flex-end; gap: 8px; margin-bottom: 18px; }
      .cp-btn-primary, .cp-btn-secondary { padding: 10px 18px; border-radius: 8px; font-size: 13.5px; font-weight: 700; font-family: inherit; cursor: pointer; transition: all 0.15s; border: none; }
      .cp-btn-primary { background: #1A3461; color: #fff; }
      .cp-btn-primary:hover { background: #1A3461; }
      .cp-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
      .cp-btn-secondary { background: #fff; color: #475569; border: 1.5px solid #E2E8F0; }
      .cp-btn-secondary:hover { border-color: #1A3461; color: #1A3461; }

      .cp-info { background: #F0FDFA; border-left: 3px solid #00A8B5; padding: 14px 18px; border-radius: 0 8px 8px 0; font-size: 12.5px; color: #134E4A; }
      .cp-info h4 { margin: 0 0 8px; font-size: 11.5px; font-weight: 800; color: #1A3461; text-transform: uppercase; letter-spacing: 0.06em; }
      .cp-info ol { margin: 0; padding-left: 20px; line-height: 1.6; }
      .cp-info code { background: rgba(0,168,181,0.12); padding: 1px 6px; border-radius: 4px; font-family: 'SF Mono', monospace; font-size: 11.5px; }

      .cp-exito { max-width: 560px; margin: 60px auto; text-align: center; padding: 32px 28px; background: #fff; border: 1px solid #E2E8F0; border-radius: 14px; }
      .cp-exito-icon { font-size: 56px; color: #16A34A; line-height: 1; margin-bottom: 12px; }
      .cp-exito h2 { margin: 0 0 8px; font-size: 22px; font-weight: 900; }
      .cp-exito p { margin: 4px 0; font-size: 14px; color: #475569; }
      .cp-exito-actions { display: flex; gap: 10px; justify-content: center; margin-top: 22px; flex-wrap: wrap; }
    `}</style>
  )
}
